import React, { type ReactNode } from "react";
import {
  CALM_GUIDANCE,
  CORE_PRINCIPLES,
  FAQ,
  FINAL_CTA,
  HERO,
  INNER_WORLD,
  LANE_DEMO_CONTEXT,
  LANE_LOGIC,
  LANGUAGES,
  LIFE_NAVIGATION,
  LIFESTYLE_COVERAGE,
  STEPS_TO_CLARITY,
  TONE_LANES,
} from "./landingContent";
import { parseEmphasis, useLandingCopy, type HeadlineKey } from "./landingCopy";

const PLANET_GLYPHS = ["☉", "☽", "☿", "♀", "♂", "♃", "♄", "♅", "♆", "✦"];
const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Sparkline per metric card: SVG path plus the position of its end dot. */
const SPARKLINES = [
  { path: "M0 44 C20 40 30 14 52 20 S86 50 108 30 S150 6 160 18", end: [160, 18] },
  { path: "M0 20 C24 22 34 46 58 40 S92 10 116 22 S148 40 160 26", end: [160, 26] },
  { path: "M0 34 C30 34 44 30 70 32 S116 30 160 28", end: [160, 28] },
  { path: "M0 40 C18 8 44 8 60 30 S104 54 124 24 S150 16 160 12", end: [160, 12] },
  { path: "M0 46 C30 40 50 34 80 26 S130 12 160 6", end: [160, 6] },
] as const;

/* ---------- shared pieces ---------- */

/** Renders a title where `<em>…</em>` marks the italic accent phrase. */
export const Emphasis: React.FC<{ markup: string }> = ({ markup }) => (
  <>
    {parseEmphasis(markup).map((part, index) =>
      part.emphasis ? (
        <em key={index}>{part.text}</em>
      ) : (
        <React.Fragment key={index}>{part.text}</React.Fragment>
      ),
    )}
  </>
);

export const SectionShell: React.FC<{ id: string; children: ReactNode }> = ({
  id,
  children,
}) => (
  <section className="al-section" id={id}>
    <div className="al-wrap">{children}</div>
  </section>
);

interface SectionHeadProps {
  eyebrow?: string;
  /** Translated title with an italic accent. Sections without one fall back to `title`. */
  titleKey?: HeadlineKey;
  title?: string;
  body?: string;
}

const SectionHead: React.FC<SectionHeadProps> = ({ eyebrow, titleKey, title, body }) => {
  const { tr, headline } = useLandingCopy();
  return (
    <div className="al-head" data-reveal>
      {eyebrow && <div className="al-eyebrow al-eyebrow--center">{tr(eyebrow)}</div>}
      <h2 className="al-h2">
        {titleKey ? <Emphasis markup={headline(titleKey)} /> : tr(title ?? "")}
      </h2>
      {body && <p className="al-lead">{tr(body)}</p>}
    </div>
  );
};

/* ---------- hero ---------- */

// "Astria — Your Personal Intelligence Companion" breaks after "— " so the name sits on its own line.
const HERO_TITLE_LINES = (HERO.headline.match(/.+?(?:— |Life |$)/g) ?? [HERO.headline]).map(
  (line) => line.trim(),
);

export const HeroSection: React.FC<{ onAction: (action: string) => void }> = ({
  onAction,
}) => {
  const { lang, tr, headline } = useLandingCopy();
  const isEnglish = lang === "en";

  return (
    <div className="al-wrap al-hero" id="hero">
      <div data-reveal>
        <div className="al-eyebrow">
          {isEnglish ? HERO.eyebrow : headline("eyebrow")}
        </div>
        <h1 className="al-hero__title">
          {HERO_TITLE_LINES.map((line) => (
            <span key={line} className="al-hero__line">
              {line}
            </span>
          ))}
        </h1>
        <p className="al-hero__sub">
          {isEnglish ? HERO.subheadline : headline("sub")}
        </p>
        <p className="al-lead al-hero__lead">
          {isEnglish ? HERO.body : headline("lead")}
        </p>
        <div className="al-hero__ctas">
          <button
            type="button"
            className="al-btn al-btn--primary"
            onClick={() => onAction(HERO.ctaPrimary.action)}
          >
            {headline("cta")} ✦
          </button>
          <button
            type="button"
            className="al-btn al-btn--ghost"
            onClick={() => onAction(HERO.ctaSecondary.action)}
          >
            {tr(HERO.ctaSecondary.label)}
          </button>
        </div>
        <div className="al-hero__principles">
          {CORE_PRINCIPLES.map((principle) => (
            <span key={principle} className="al-chip al-chip--soft">
              {tr(principle)}
            </span>
          ))}
        </div>
      </div>

      <div className="al-hero__orbit" data-reveal>
        <div className="al-hero__glow" aria-hidden="true" />
        <img
          className="al-hero__logo"
          src="/logo_1.png"
          alt="Astria"
          width={1254}
          height={1254}
          decoding="async"
        />
      </div>
    </div>
  );
};

/* ---------- card grids ---------- */

type GridItem = string | { title: string; text?: string };

interface FeatureGridSectionProps {
  id: string;
  titleKey?: HeadlineKey;
  content: {
    title?: string;
    eyebrow?: string;
    body?: string;
    footnote?: string;
    items: readonly GridItem[];
  };
  /** Shifts which planet glyph the first card gets. */
  glyphOffset?: number;
  glyphs?: readonly string[];
  /** Small centred tiles without index numbers or body text. */
  compact?: boolean;
  /** Extra block rendered under the grid (lane demo, language lists…). */
  children?: ReactNode;
}

const columnModifier = (count: number, compact: boolean): string => {
  if (count === 2) return "al-grid--2";
  if (count === 4) return "al-grid--4";
  if (count === 5) return "al-grid--5";
  if (count === 6 && compact) return "al-grid--6";
  return "";
};

export const FeatureGridSection: React.FC<FeatureGridSectionProps> = ({
  id,
  titleKey,
  content,
  glyphOffset = 0,
  glyphs = PLANET_GLYPHS,
  compact = false,
  children,
}) => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id={id}>
      <SectionHead
        eyebrow={content.eyebrow}
        titleKey={titleKey}
        title={content.title}
        body={content.body}
      />
      <div className={`al-grid ${columnModifier(content.items.length, compact)}`}>
        {content.items.map((item, index) => {
          const { title, text } = typeof item === "string" ? { title: item, text: undefined } : item;
          return (
            <div
              key={title}
              className={`al-card ${compact ? "al-card--compact" : ""}`}
              data-reveal
            >
              <div className="al-card__glyph" aria-hidden="true">
                {glyphs[(index + glyphOffset) % glyphs.length]}
              </div>
              {!compact && <span className="al-card__index">{pad2(index + 1)}</span>}
              <h3 className="al-card__title">{tr(title)}</h3>
              {text && <p className="al-card__text">{tr(text)}</p>}
            </div>
          );
        })}
      </div>
      {content.footnote && (
        <p className="al-footnote" data-reveal>
          {tr(content.footnote)}
        </p>
      )}
      {children}
    </SectionShell>
  );
};

/* ---------- tone lanes ---------- */

export const LaneDemo: React.FC<{
  lane: string;
  onLaneChange: (code: string) => void;
}> = ({ lane, onLaneChange }) => {
  const { tr } = useLandingCopy();
  const current = TONE_LANES.find((item) => item.code === lane) ?? TONE_LANES[0];

  return (
    <div className="al-lane-demo" data-reveal>
      <div className="al-lane-demo__head">
        <span>{tr(LANE_DEMO_CONTEXT)}</span>
        <small>{tr(LANE_LOGIC)}</small>
      </div>
      <div className="al-lane-demo__tabs" role="tablist">
        {TONE_LANES.map((item) => (
          <button
            key={item.code}
            type="button"
            role="tab"
            aria-selected={item.code === current.code}
            className={item.code === current.code ? "is-active" : ""}
            onClick={() => onLaneChange(item.code)}
          >
            <b>{item.label}</b>
          </button>
        ))}
      </div>
      <div className="al-lane-demo__output" aria-live="polite">
        <small>{current.label}</small>
        <p>“{tr(current.sample)}”</p>
      </div>
    </div>
  );
};

/** Supported languages, tone lanes and life areas — the block under the "global" cards. */
export const GlobalReach: React.FC = () => {
  const { tr } = useLandingCopy();
  return (
    <div className="al-reach" data-reveal>
      <div className="al-reach__title">
        <span>{tr("Supported languages")}</span>
      </div>
      <div className="al-chips al-chips--spaced">
        {LANGUAGES.map((language) => (
          <span key={language.code} className="al-chip">
            {tr(language.englishName)}
          </span>
        ))}
      </div>

      <div className="al-reach__title al-reach__title--last">
        <span>{tr("Across every part of life")}</span>
      </div>
      <div className="al-chips">
        {LIFESTYLE_COVERAGE.map((area) => (
          <span key={area} className="al-chip al-chip--soft">
            {tr(area)}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- life navigation / calm guidance ---------- */

export const LifeNavigationSection: React.FC = () => {
  const { tr, headline } = useLandingCopy();
  return (
    <SectionShell id="life_navigation">
      <div className="al-two">
        <div data-reveal>
          <div className="al-eyebrow">{tr(LIFE_NAVIGATION.eyebrow)}</div>
          <h2 className="al-h2">
            <Emphasis markup={headline("sysT")} />
          </h2>
          <p className="al-lead">{tr(LIFE_NAVIGATION.body)}</p>
        </div>
        <div className="al-pillars">
          {LIFE_NAVIGATION.pillars.map((pillar, index) => (
            <div key={pillar.title} className="al-pillar" data-reveal>
              <small>{pad2(index + 1)}</small>
              <b>{tr(pillar.title)}</b>
              <p>{tr(pillar.text)}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
};

export const CalmGuidanceSection: React.FC = () => {
  const { tr, headline } = useLandingCopy();
  return (
    <SectionShell id="calm_guidance">
      <div className="al-statement" data-reveal>
        <span className="al-hairline" />
        <h2 className="al-h2">
          <Emphasis markup={headline("calmT")} />
        </h2>
        <span className="al-hairline" />
      </div>
      <div className="al-moments">
        {CALM_GUIDANCE.moments.map((moment) => (
          <div key={moment.label} className="al-moment" data-reveal>
            <b>{tr(moment.label)}</b>
            <p>{tr(moment.text)}</p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

/* ---------- metrics / steps ---------- */

export const MetricsSection: React.FC = () => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id="inner_world">
      <SectionHead
        eyebrow={INNER_WORLD.eyebrow}
        titleKey="innerT"
        body={INNER_WORLD.body}
      />
      <div className="al-metrics">
        {INNER_WORLD.metrics.map((metric, index) => {
          const spark = SPARKLINES[index % SPARKLINES.length];
          return (
            <div key={metric.label} className="al-metric" data-reveal>
              <small>
                {tr("Signal")} {pad2(index + 1)}
              </small>
              <svg viewBox="0 0 160 60" preserveAspectRatio="none" aria-hidden="true">
                <path d={spark.path} />
                <circle cx={spark.end[0]} cy={spark.end[1]} r="2.6" />
              </svg>
              <b>{tr(metric.label)}</b>
              <em className="al-metric__value">{tr(metric.sampleValue)}</em>
              <p>{tr(metric.description)}</p>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
};

export const StepsSection: React.FC = () => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id="steps_to_clarity">
      <SectionHead eyebrow={STEPS_TO_CLARITY.eyebrow} titleKey="stepsT" />
      <div className="al-steps">
        {STEPS_TO_CLARITY.steps.map((step) => (
          <div key={step.number} className="al-step" data-reveal>
            <div className="al-step__number">{step.number}</div>
            <h3 className="al-step__title">{tr(step.title)}</h3>
            <p className="al-step__text">{tr(step.text)}</p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

/* ---------- FAQ / closing banner ---------- */

export const FaqSection: React.FC = () => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id="faq">
      <SectionHead eyebrow={FAQ.eyebrow} titleKey="beforeT" />
      <div className="al-faq">
        {FAQ.items.map((item) => (
          <details key={item.q} className="al-faq__item" data-reveal>
            <summary>{tr(item.q)}</summary>
            <p>{tr(item.a)}</p>
          </details>
        ))}
      </div>
    </SectionShell>
  );
};

export const FinalCtaSection: React.FC<{ onAction: (action: string) => void }> = ({
  onAction,
}) => {
  const { tr, headline } = useLandingCopy();
  return (
    <SectionShell id="final_cta">
      <div className="al-final" data-reveal>
        <div className="al-eyebrow al-eyebrow--center">{tr(FINAL_CTA.eyebrow)}</div>
        <h2 className="al-h2">
          <Emphasis markup={headline("finalT")} />
        </h2>
        <button
          type="button"
          className="al-btn al-btn--primary"
          onClick={() => onAction(FINAL_CTA.cta.action)}
        >
          {headline("finalCta")} ✦
        </button>
      </div>
    </SectionShell>
  );
};
